import type { Overview, ProjectSummary, ShareSummary } from '@/model/dataConsole/dto.generated';
import { providerProjectLabel } from '@/model/etlSource/etlSource.model';
import type {
  CoOwnerView,
  ConsoleProjectView,
  ConsoleShareView,
  ConsoleView,
} from '@/viewmodel/features/dataConsole/types';

const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name);

const byEnds = (a: ConsoleShareView, b: ConsoleShareView): number =>
  a.providerName.localeCompare(b.providerName) || a.consumerName.localeCompare(b.consumerName);

function toProjectView(project: ProjectSummary, name: string): ConsoleProjectView {
  return {
    id: project.id,
    name,
    requestId: project.request_id,
    sources: [...(project.sources ?? [])].sort(byName).map((source) => ({
      id: source.id,
      name: source.name,
      slug: source.slug,
      externalSourceId: source.external_source_id.trim(),
      isFile: source.is_file,
      tableNames: source.table_names ?? [],
      engine: source.engine,
      status: source.status,
      tableCount: (source.table_names ?? []).length,
      lastSyncAt: source.last_successful_sync_at,
    })),
  };
}

export function toConsoleView(overview: Overview | undefined, storeName: string): ConsoleView {
  const all = overview?.projects ?? [];
  const storeSummary = all.find((project) => project.is_store);
  const nameOf = (id: string, raw: string): string =>
    id === storeSummary?.id ? storeName : providerProjectLabel(raw);
  const projects = all
    .filter((project) => !project.is_store)
    .map((project) => toProjectView(project, providerProjectLabel(project.name)))
    .sort(byName);
  const coOwners: CoOwnerView[] = (overview?.co_owners ?? [])
    .map((grant) => ({
      id: grant.id,
      providerId: grant.provider_project_id,
      providerName: nameOf(grant.provider_project_id, grant.provider_name),
      granteeId: grant.grantee_project_id,
      granteeName: nameOf(grant.grantee_project_id, grant.grantee_name),
      createdAt: grant.created_at,
    }))
    .sort(
      (a, b) =>
        a.providerName.localeCompare(b.providerName) || a.granteeName.localeCompare(b.granteeName),
    );
  const shares = (overview?.shares ?? [])
    .map(
      (share: ShareSummary): ConsoleShareView => ({
        id: share.id,
        providerId: share.provider_project_id,
        providerName: nameOf(share.provider_project_id, share.provider_name),
        consumerId: share.consumer_project_id,
        consumerName: nameOf(share.consumer_project_id, share.consumer_name),
        tableNames: share.table_names ?? [],
        expiresAt: share.expires_at,
      }),
    )
    .sort(byEnds);

  return {
    projects,
    store: storeSummary ? toProjectView(storeSummary, storeName) : null,
    shares,
    coOwners,
  };
}
